import React, { useState, useEffect, useCallback } from 'react';
import { reimbursementAPI, uploadAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { DatePicker } from '../../components/ui/date-picker';
import {
  Plus, Upload, FileImage, X, Clock, CheckCircle, XCircle,
  IndianRupee, Receipt, Eye, Trash2, Pencil, ArrowLeft,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_MAP = {
  submitted: { label: 'Submitted', cls: 'bg-yellow-100 text-yellow-700' },
  under_review: { label: 'Under Review', cls: 'bg-blue-100 text-blue-700' },
  approved: { label: 'Approved', cls: 'bg-green-100 text-green-700' },
  partially_approved: { label: 'Partial', cls: 'bg-orange-100 text-orange-700' },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700' },
  paid: { label: 'Paid', cls: 'bg-emerald-100 text-emerald-800' },
};

const getFileUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

const emptyItem = { date_incurred: '', amount: '', category: '', description: '', bill_urls: [] };

const Reimbursements = () => {
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitOpen, setIsSubmitOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const [filterMonth, setFilterMonth] = useState(new Date().toISOString().slice(0, 7));
  const [editingClaimId, setEditingClaimId] = useState(null);

  const [title, setTitle] = useState('');
  const [items, setItems] = useState([{ ...emptyItem }]);

  const [previewUrl, setPreviewUrl] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [claimRes, catRes] = await Promise.all([
        reimbursementAPI.getMy(),
        reimbursementAPI.getCategories(),
      ]);
      setClaims(claimRes.data);
      setCategories(catRes.data);
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    const iv = setInterval(fetchData, 30000);
    return () => clearInterval(iv);
  }, [fetchData]);

  const updateItem = (idx, field, value) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  };
  const addItem = () => setItems(prev => [...prev, { ...emptyItem }]);
  const removeItem = (idx) => { if (items.length > 1) setItems(prev => prev.filter((_, i) => i !== idx)); };

  const handleBillUpload = async (e, idx) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingIdx(idx);
    try {
      const res = await uploadAPI.uploadDocument(file);
      updateItem(idx, 'bill_urls', [...items[idx].bill_urls, res.data.url]);
    } catch { alert('Upload failed'); }
    finally { setUploadingIdx(null); }
  };

  const removeBill = (iIdx, bIdx) => {
    updateItem(iIdx, 'bill_urls', items[iIdx].bill_urls.filter((_, i) => i !== bIdx));
  };

  const formatAmount = (val) => {
    const num = parseFloat(val);
    return isNaN(num) ? '0.00' : num.toFixed(2);
  };

  const handleSubmit = async () => {
    if (!title.trim()) { alert('Enter a title'); return; }
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (!it.date_incurred || !it.amount || !it.category || !it.description) { alert(`Fill all fields for item #${i + 1}`); return; }
      if (it.bill_urls.length === 0) { alert(`Attach at least one bill for item #${i + 1}`); return; }
    }
    setSaving(true);
    try {
      if (editingClaimId) {
        // Editing a single existing claim
        const it = items[0];
        await reimbursementAPI.update(editingClaimId, {
          date_incurred: it.date_incurred,
          amount: parseFloat(formatAmount(it.amount)),
          category: it.category,
          description: it.description,
          bill_urls: it.bill_urls,
        });
      } else {
        await reimbursementAPI.submit({
          title,
          items: items.map(it => ({ ...it, amount: parseFloat(formatAmount(it.amount)) })),
        });
      }
      setIsSubmitOpen(false);
      setTitle(''); setItems([{ ...emptyItem }]); setEditingClaimId(null);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleEditClaim = (claim) => {
    setEditingClaimId(claim.id);
    setTitle(claim.report_title || claim.description);
    setItems([{
      date_incurred: claim.date_incurred,
      amount: String(claim.amount_claimed),
      category: claim.category,
      description: claim.description,
      bill_urls: claim.bill_urls || [],
    }]);
    setIsSubmitOpen(true);
  };

  const handleDeleteClaim = async (claimId) => {
    if (!window.confirm('Delete this expense claim? This cannot be undone.')) return;
    try {
      await reimbursementAPI.delete(claimId);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const filtered = claims.filter(c =>
    c.date_incurred?.startsWith(filterMonth) || c.created_at?.startsWith(filterMonth)
  );

  const pendingCount = filtered.filter(c => ['submitted', 'under_review'].includes(c.status)).length;
  const totalApproved = filtered
    .filter(c => ['approved', 'partially_approved', 'paid'].includes(c.status))
    .reduce((s, c) => s + (c.approved_amount || 0), 0);

  const totalItems = items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);

  // Group claims by report_title for display
  const grouped = {};
  filtered.forEach(c => {
    const key = c.report_title || c.report_id || c.id;
    if (!grouped[key]) grouped[key] = { title: c.report_title || c.description || c.category, claims: [] };
    grouped[key].claims.push(c);
  });

  return (
    <div className="space-y-5" data-testid="employee-reimbursements-page">
      <button onClick={() => navigate('/portal')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#1D35A0] transition-colors mb-1" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Back to Home
      </button>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-[#1D35A0]">My Expense Claims</h2>
          <p className="text-sm text-slate-500">Submit and track your reimbursement claims</p>
        </div>
        <Button onClick={() => { setTitle(''); setItems([{ ...emptyItem }]); setEditingClaimId(null); setIsSubmitOpen(true); }} className="bg-[#1D35A0] hover:bg-[#152A80] text-white" data-testid="submit-claim-btn">
          <Plus className="w-4 h-4 mr-2" /> Submit Expense Report
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <div className="bg-white rounded-lg border p-2.5 sm:p-3 shadow-sm flex items-center gap-2">
          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0"><Receipt className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-600" /></div>
          <div className="min-w-0"><p className="text-lg sm:text-xl font-bold text-[#1D35A0]" data-testid="total-claims">{claims.length}</p><p className="text-[10px] text-slate-500 truncate">Total</p></div>
        </div>
        <div className="bg-white rounded-lg border p-2.5 sm:p-3 shadow-sm flex items-center gap-2">
          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-yellow-100 flex items-center justify-center shrink-0"><Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-yellow-600" /></div>
          <div className="min-w-0"><p className="text-lg sm:text-xl font-bold text-[#1D35A0]" data-testid="pending-claims">{pendingCount}</p><p className="text-[10px] text-slate-500 truncate">Pending</p></div>
        </div>
        <div className="bg-white rounded-lg border p-2.5 sm:p-3 shadow-sm flex items-center gap-2">
          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-green-100 flex items-center justify-center shrink-0"><IndianRupee className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-600" /></div>
          <div className="min-w-0"><p className="text-base sm:text-xl font-bold text-[#1D35A0] truncate" data-testid="total-approved">Rs. {totalApproved.toLocaleString('en-IN', { minimumFractionDigits: 0 })}</p><p className="text-[10px] text-slate-500 truncate">Approved</p></div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Input type="month" value={filterMonth} onChange={e => setFilterMonth(e.target.value)} className="w-44 text-slate-400" data-testid="filter-month" />
        <span className="text-sm text-slate-400">{filtered.length} claim(s)</span>
      </div>

      {/* Claims grouped by report */}
      <div className="space-y-5">
        {loading ? (
          <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1D35A0]" /></div>
        ) : Object.keys(grouped).length === 0 ? (
          <div className="bg-white rounded-lg border p-6 text-center text-slate-400"><Receipt className="w-8 h-8 mx-auto mb-1.5" /><p>No claims for this month</p></div>
        ) : Object.entries(grouped).map(([key, group]) => (
          <div key={key} className="bg-white rounded-lg border shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b bg-slate-50/50">
              <p className="text-sm font-semibold text-[#1D35A0]">{group.title}</p>
              <p className="text-xs text-slate-400">{group.claims.length} expense(s) — Total: Rs. {group.claims.reduce((s, c) => s + c.amount_claimed, 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
            </div>
            <div className="divide-y">
              {group.claims.map(claim => {
                const st = STATUS_MAP[claim.status] || { label: claim.status, cls: 'bg-slate-100 text-slate-700' };
                return (
                  <div key={claim.id} className="px-3 sm:px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4" data-testid={`my-claim-row-${claim.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-[#1D35A0]">{claim.description}</span>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-50 text-purple-700">{claim.category}</span>
                        <span className="text-xs text-slate-400">{claim.date_incurred}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>
                        {claim.admin_note && <span className="text-xs text-slate-500 italic truncate max-w-[200px]">"{claim.admin_note}"</span>}
                      </div>
                    </div>
                    <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3">
                      <div className="text-left sm:text-right shrink-0">
                        <p className="text-sm sm:text-base font-bold text-[#1D35A0]">Rs. {claim.amount_claimed?.toFixed(2)}</p>
                        {claim.approved_amount != null && claim.approved_amount !== claim.amount_claimed && (
                          <p className="text-xs sm:text-sm font-semibold text-green-600">Approved: Rs. {claim.approved_amount?.toFixed(2)}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => { setSelectedClaim(claim); setIsDetailOpen(true); }} className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-blue-600" data-testid={`view-my-claim-${claim.id}`}>
                          <Eye className="w-4 h-4" />
                        </button>
                        {claim.status === 'submitted' && (
                          <>
                            <button onClick={() => handleEditClaim(claim)} className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-amber-600" data-testid={`edit-my-claim-${claim.id}`}>
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDeleteClaim(claim.id)} className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-red-600" data-testid={`delete-my-claim-${claim.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Submit Dialog */}
      <Dialog open={isSubmitOpen} onOpenChange={setIsSubmitOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="submit-claim-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1D35A0]">{editingClaimId ? 'Edit Expense' : 'Submit Expense Report'}</DialogTitle>
            <DialogDescription>{editingClaimId ? 'Update your expense claim details.' : 'Add one or more expenses. Each expense will be reviewed individually.'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 mt-4">
            <div className="space-y-2">
              <Label>Report Title *</Label>
              <Input placeholder="e.g. Mumbai Client Visit - March 2026" value={title} onChange={e => setTitle(e.target.value)} data-testid="report-title" />
            </div>
            {items.map((item, idx) => (
              <div key={idx} className="border rounded-lg p-4 space-y-3 bg-slate-50/50">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-[#1D35A0]">Expense #{idx + 1}</p>
                  {items.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} className="text-red-500 h-7 px-2" data-testid={`remove-item-${idx}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Date *</Label>
                    <DatePicker value={item.date_incurred} onChange={v => updateItem(idx, 'date_incurred', v)} placeholder="Pick date" data-testid={`item-date-${idx}`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Amount (Rs.) *</Label>
                    <Input
                      type="text" inputMode="decimal"
                      placeholder="0.00"
                      value={item.amount}
                      onChange={e => {
                        const v = e.target.value.replace(/[^0-9.]/g, '');
                        const parts = v.split('.');
                        const cleaned = parts[0] + (parts.length > 1 ? '.' + parts[1].slice(0, 2) : '');
                        updateItem(idx, 'amount', cleaned);
                      }}
                      onBlur={() => { if (item.amount) updateItem(idx, 'amount', formatAmount(item.amount)); }}
                      className="[appearance:textfield]"
                      data-testid={`item-amount-${idx}`}
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Category *</Label>
                  <Select value={item.category} onValueChange={v => updateItem(idx, 'category', v)}>
                    <SelectTrigger data-testid={`item-category-${idx}`}><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Description *</Label>
                  <Input placeholder="Brief description" value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} data-testid={`item-desc-${idx}`} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Bills / Receipts *</Label>
                  {item.bill_urls.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {item.bill_urls.map((url, bi) => (
                        <div key={bi} className="relative w-16 h-16 border rounded overflow-hidden group">
                          <img src={getFileUrl(url)} alt="Bill" className="w-full h-full object-cover" />
                          <button onClick={() => removeBill(idx, bi)} className="absolute top-0 right-0 bg-red-500 text-white rounded-bl p-0.5 opacity-0 group-hover:opacity-100"><X className="w-3 h-3" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                  <label className="flex items-center gap-2 px-3 py-2 border border-dashed rounded-lg text-xs text-slate-500 cursor-pointer hover:border-blue-400 hover:text-blue-500">
                    <Upload className="w-3.5 h-3.5" />
                    {uploadingIdx === idx ? 'Uploading...' : 'Upload bill / receipt'}
                    <input type="file" className="hidden" accept="image/*,.pdf" onChange={e => handleBillUpload(e, idx)} disabled={uploadingIdx !== null} />
                  </label>
                </div>
              </div>
            ))}
            {!editingClaimId && (
              <Button variant="outline" onClick={addItem} className="w-full border-dashed" data-testid="add-item-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Another Expense
              </Button>
            )}
            {items.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-right">
                <span className="text-sm text-blue-700 font-semibold">Total: Rs. {totalItems.toFixed(2)}</span>
                <span className="text-xs text-blue-500 ml-2">({items.length} item{items.length > 1 ? 's' : ''})</span>
              </div>
            )}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setIsSubmitOpen(false)}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={saving} className="bg-[#1D35A0] hover:bg-[#152A80] text-white px-8" data-testid="submit-claim-form-btn">
                {saving ? 'Submitting...' : editingClaimId ? 'Update Expense' : 'Submit Report'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={v => { setIsDetailOpen(v); if (!v) setSelectedClaim(null); }}>
        <DialogContent className="max-w-md" data-testid="my-claim-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1D35A0]">Claim Details</DialogTitle>
            <DialogDescription>{selectedClaim?.description}</DialogDescription>
          </DialogHeader>
          {selectedClaim && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs text-slate-500">Category</p><p className="text-sm font-medium">{selectedClaim.category}</p></div>
                <div><p className="text-xs text-slate-500">Date</p><p className="text-sm">{selectedClaim.date_incurred}</p></div>
                <div><p className="text-xs text-slate-500">Amount Claimed</p><p className="text-lg font-bold text-[#1D35A0]">Rs. {selectedClaim.amount_claimed?.toFixed(2)}</p></div>
                <div><p className="text-xs text-slate-500">Status</p>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${(STATUS_MAP[selectedClaim.status] || {}).cls}`}>
                    {(STATUS_MAP[selectedClaim.status] || {}).label}
                  </span>
                </div>
              </div>
              {selectedClaim.approved_amount != null && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-xs text-green-600">Approved Amount</p>
                  <p className="text-xl font-bold text-green-700">Rs. {selectedClaim.approved_amount?.toFixed(2)}</p>
                </div>
              )}
              {selectedClaim.admin_note && (
                <div className="bg-slate-50 p-3 rounded-lg"><p className="text-xs text-slate-500">Admin Note</p><p className="text-sm">{selectedClaim.admin_note}</p></div>
              )}
              {selectedClaim.payment_reference && (
                <div><p className="text-xs text-slate-500">Payment Ref</p><p className="text-sm font-mono">{selectedClaim.payment_reference}</p></div>
              )}
              {/* Bills */}
              {selectedClaim.bill_urls?.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 mb-2">Receipts ({selectedClaim.bill_urls.length})</p>
                  <div className="grid grid-cols-3 gap-2">
                    {selectedClaim.bill_urls.map((url, i) => {
                      const full = getFileUrl(url);
                      const isPdf = url.endsWith('.pdf');
                      return (
                        <button key={i} onClick={() => setPreviewUrl(full)} className="border rounded-lg overflow-hidden hover:ring-2 ring-blue-400 cursor-pointer bg-slate-100">
                          {isPdf ? (
                            <div className="w-full h-20 bg-red-50 flex items-center justify-center"><FileImage className="w-4 h-4 text-red-400" /><span className="text-xs ml-1">PDF</span></div>
                          ) : (
                            <img src={full} alt={`Bill ${i + 1}`} className="w-full h-20 object-cover" onError={e => { e.target.onerror = null; e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<div class="w-full h-20 flex items-center justify-center text-slate-300 text-xs">No preview</div>'; }} />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Preview Modal */}
      {previewUrl && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setPreviewUrl(null)}>
          <button onClick={() => setPreviewUrl(null)} className="absolute top-4 right-4 text-white hover:text-slate-300 z-10">
            <X className="w-8 h-8" />
          </button>
          <div className="max-w-3xl max-h-[85vh] relative" onClick={e => e.stopPropagation()}>
            {previewUrl.endsWith('.pdf') ? (
              <iframe src={previewUrl} className="w-[700px] h-[80vh] bg-white rounded-lg" title="PDF" />
            ) : (
              <img src={previewUrl} alt="Bill Preview" className="max-w-full max-h-[85vh] rounded-lg shadow-2xl" onError={e => { e.target.alt = 'Image not available'; }} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Reimbursements;
